import type { PointerSpotlight } from 'types/spotlight'

const HOVER_QUERY = '(hover: hover)'
// The overlay is a child, so the variables are set on the card and inherited down.
const CARD_SELECTOR = '[data-spotlight]'
const X_PROPERTY = '--spotlight-x'
const Y_PROPERTY = '--spotlight-y'

type SpotlightPosition = {
  card: HTMLElement,
  x: number,
  y: number
}

// Pixels rather than percentages: any layer anchored to the card top-left corner can reuse the variables as-is.
function getPosition(card: HTMLElement, event: PointerEvent): SpotlightPosition {
  const box = card.getBoundingClientRect()

  return {
    card,
    x: event.clientX - box.left,
    y: event.clientY - box.top
  }
}

// One delegated listener rather than one per card: the cards are re-created on every page transition.
export function usePointerSpotlight(): PointerSpotlight {
  let frame = 0
  let position: SpotlightPosition | undefined = undefined

  function paint(): void {
    frame = 0

    if (!position) {
      return
    }

    position.card.style.setProperty(X_PROPERTY, `${position.x.toFixed(2)}px`)
    position.card.style.setProperty(Y_PROPERTY, `${position.y.toFixed(2)}px`)
    position = undefined
  }

  function handlePointerMove(event: PointerEvent): void {
    const target = event.target as HTMLElement | null
    const card = target?.closest<HTMLElement>(CARD_SELECTOR)

    if (!card) {
      return
    }

    position = getPosition(card, event)

    if (frame) {
      return
    }

    frame = requestAnimationFrame(paint)
  }

  function start(): void {
    // A touch pointer would light the border on tap and leave it lit.
    if (!window.matchMedia(HOVER_QUERY).matches) {
      return
    }

    document.addEventListener('pointermove', handlePointerMove, { passive: true })
  }

  return {
    start
  }
}
